import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Upload, Loader2, CheckCircle, AlertCircle, 
  Copy, Share2, Mail, Trash2, Clock, DollarSign,
  FileType, Calendar, Tag, ChevronRight, X, MessageCircle, Download,
  Link2, Eye, UserPlus, Search, Check, ExternalLink, User, History
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Document type badge colors
const DOC_TYPE_COLORS = {
  'Insurance Policy': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Training Brochure': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Real Estate Document': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Legal Contract': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial Document': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Marketing Material': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Technical Documentation': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'General Document': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const DocAnalyzer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Sharing state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  
  // Attach to Lead state
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [attachingLead, setAttachingLead] = useState(false);

  useEffect(() => {
    fetchDocuments();
    
    // Check if viewing a specific document from URL
    const viewId = searchParams.get('view');
    if (viewId) {
      loadDocumentById(viewId);
    }
  }, [searchParams]);
  
  const loadDocumentById = async (docId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDoc(res.data);
    } catch (error) {
      console.error('Failed to load document:', error);
      toast.error('Failed to load document');
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Unsupported file type. Please upload PDF, Word, or Image files.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setAnalyzing(false);

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress for upload
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 40) {
            clearInterval(progressInterval);
            return 40;
          }
          return prev + 10;
        });
      }, 200);

      // Upload and analyze in one request
      setAnalyzing(true);
      setUploadProgress(50);

      const res = await axios.post(
        `${BACKEND_URL}/api/documents/upload-and-analyze`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success('Document analyzed successfully!');
      setSelectedDoc(res.data);
      fetchDocuments();

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze document');
    } finally {
      setUploading(false);
      setAnalyzing(false);
      setUploadProgress(0);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const copyBulletPoints = () => {
    if (!selectedDoc?.analysis?.key_points) return;
    const { title, summary, key_points, dates, pricing } = selectedDoc.analysis;
    
    let text = `📄 ${title}\n\n`;
    text += `${summary}\n\n`;
    text += `Key Points:\n`;
    key_points.forEach((point, i) => {
      text += `${i + 1}. ${point}\n`;
    });
    
    if (dates && dates.length > 0) {
      text += `\nImportant Dates:\n`;
      dates.forEach(date => {
        text += `• ${date}\n`;
      });
    }
    
    if (pricing && pricing.length > 0) {
      text += `\nPricing:\n`;
      pricing.forEach(price => {
        text += `• ${price}\n`;
      });
    }
    
    text += `\n---\nAnalyzed by AI Lead Mantra`;
    copyToClipboard(text);
  };

  const shareViaWhatsApp = () => {
    if (!selectedDoc?.analysis) return;
    const { title, summary, key_points, dates, pricing } = selectedDoc.analysis;
    
    let message = `📄 *${title}*\n\n`;
    message += `${summary}\n\n`;
    message += `*Key Points:*\n`;
    
    // Include ALL key points
    key_points.forEach((point, i) => {
      message += `${i + 1}. ${point}\n`;
    });
    
    // Include dates if available
    if (dates && dates.length > 0) {
      message += `\n*Important Dates:*\n`;
      dates.forEach(date => {
        message += `• ${date}\n`;
      });
    }
    
    // Include pricing if available
    if (pricing && pricing.length > 0) {
      message += `\n*Pricing:*\n`;
      pricing.forEach(price => {
        message += `• ${price}\n`;
      });
    }
    
    message += `\n_Analyzed by AI Lead Mantra_`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const shareViaEmail = () => {
    if (!selectedDoc?.analysis) return;
    const { title, summary, key_points, dates, pricing } = selectedDoc.analysis;
    
    const subject = `Document Analysis: ${title}`;
    let body = `${title}\n\n`;
    body += `${summary}\n\n`;
    body += `Key Points:\n`;
    key_points.forEach((point, i) => {
      body += `${i + 1}. ${point}\n`;
    });
    
    if (dates && dates.length > 0) {
      body += `\nImportant Dates:\n`;
      dates.forEach(date => {
        body += `• ${date}\n`;
      });
    }
    
    if (pricing && pricing.length > 0) {
      body += `\nPricing:\n`;
      pricing.forEach(price => {
        body += `• ${price}\n`;
      });
    }
    
    body += `\n---\nAnalyzed by AI Lead Mantra`;
    
    // Use window.open for better compatibility
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_self');
  };

  const downloadPDF = async () => {
    if (!selectedDoc?.analysis) return;
    
    setGeneratingPDF(true);
    toast.info('Generating your PDF...', { duration: 2000 });
    
    try {
      const { title, document_type, summary, key_points, dates, pricing } = selectedDoc.analysis;
      const formattedDate = new Date().toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short', 
        year: 'numeric'
      });
      
      // Create hidden div with compact HTML content
      const element = document.createElement('div');
      element.style.cssText = `
        position: fixed;
        left: -9999px;
        top: 0;
        width: 794px;
        background: white;
        font-family: Calibri, 'Trebuchet MS', 'Gill Sans', sans-serif;
      `;
      
      // Split key points into 2 equal columns
      const midPoint = Math.ceil((key_points?.length || 0) / 2);
      const leftPoints = key_points?.slice(0, midPoint) || [];
      const rightPoints = key_points?.slice(midPoint) || [];
      
      element.innerHTML = `
<div style="font-family: Calibri, 'Trebuchet MS', 'Gill Sans', sans-serif; width: 794px; background: white; color: #222;">

  <!-- HEADER - 3 COLUMN LAYOUT (Table for html2canvas compatibility) -->
  <table style="width: 100%; background: #0a5c44; border-collapse: collapse;">
    <tr>
      <td style="width: 33.33%; padding: 18px 30px; vertical-align: middle; text-align: left;">
        <span style="color: white; font-size: 22px; font-weight: bold;">AI Lead Mantra</span>
      </td>
      <td style="width: 33.33%; padding: 18px 0; vertical-align: middle; text-align: center;">
        <span style="color: white; font-size: 13px;">Document Analysis Report</span>
      </td>
      <td style="width: 33.33%; padding: 18px 30px; vertical-align: middle; text-align: right;">
        <div style="margin-bottom: 4px;">
          <span style="background: rgba(255,255,255,0.2); color: white; padding: 4px 12px; border-radius: 12px; font-size: 10px; display: inline-block;">${document_type}</span>
        </div>
        <div style="color: rgba(255,255,255,0.85); font-size: 11px;">${formattedDate}</div>
      </td>
    </tr>
  </table>

  <!-- TITLE & SUMMARY - FULL TEXT NO TRUNCATION -->
  <div style="background: #f5f9f7; padding: 14px 30px; border-left: 3px solid #0a5c44;">
    <div style="color: #0a5c44; font-size: 14px; font-weight: bold; margin-bottom: 6px; line-height: 1.3;">${title}</div>
    <div style="color: #444; font-size: 10px; line-height: 1.5;">${summary}</div>
  </div>

  <!-- MAIN CONTENT -->
  <div style="padding: 14px 30px 10px;">

    <!-- KEY POINTS - PERFECT 2 COLUMNS -->
    <div style="margin-bottom: 14px;">
      <div style="color: #0a5c44; font-size: 11px; font-weight: bold; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #0a5c44;">KEY POINTS</div>
      <table style="width: 100%; border-collapse: collapse; table-layout: fixed;">
        <tr>
          <td style="width: 50%; vertical-align: top; padding-right: 12px;">
            ${leftPoints.map((point, i) => `
              <div style="margin-bottom: 5px; font-size: 10px; line-height: 1.4;">
                <span style="color: #0a5c44; font-weight: bold; display: inline-block; width: 16px;">${i + 1}.</span>
                <span style="color: #333;">${point}</span>
              </div>
            `).join('')}
          </td>
          <td style="width: 50%; vertical-align: top; padding-left: 12px; border-left: 1px solid #ddd;">
            ${rightPoints.map((point, i) => `
              <div style="margin-bottom: 5px; font-size: 10px; line-height: 1.4;">
                <span style="color: #0a5c44; font-weight: bold; display: inline-block; width: 16px;">${midPoint + i + 1}.</span>
                <span style="color: #333;">${point}</span>
              </div>
            `).join('')}
          </td>
        </tr>
      </table>
    </div>

    <!-- DATES & PRICING - PERFECT SIDE BY SIDE -->
    ${(dates?.length > 0 || pricing?.length > 0) ? `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 14px; table-layout: fixed;">
      <tr>
        ${dates?.length > 0 ? `
        <td style="width: 50%; vertical-align: top; padding-right: 12px;">
          <div style="color: #0a5c44; font-size: 11px; font-weight: bold; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #0a5c44;">IMPORTANT DATES</div>
          ${dates.map(date => `
            <div style="margin-bottom: 4px; font-size: 10px; line-height: 1.4; padding-left: 2px;">
              <span style="color: #0a5c44; margin-right: 6px;">•</span><span style="color: #333;">${date}</span>
            </div>
          `).join('')}
        </td>
        ` : '<td style="width: 50%;"></td>'}
        ${pricing?.length > 0 ? `
        <td style="width: 50%; vertical-align: top; padding-left: 12px; border-left: 1px solid #ddd;">
          <div style="color: #0a5c44; font-size: 11px; font-weight: bold; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #0a5c44;">PRICING DETAILS</div>
          <div style="background: #e8f5e9; border: 1px solid #c8e6c9; border-radius: 4px; padding: 8px;">
            ${pricing.map(price => `
              <div style="margin-bottom: 3px; font-size: 10px; line-height: 1.4;">
                <span style="color: #0a5c44; margin-right: 6px;">→</span><span style="color: #222;">${price.replace(/₹/g, 'Rs.')}</span>
              </div>
            `).join('')}
          </div>
        </td>
        ` : '<td style="width: 50%;"></td>'}
      </tr>
    </table>
    ` : ''}

    <!-- CTA BOX -->
    <table style="width: 100%; background: #0a5c44; border-radius: 6px; border-collapse: collapse;">
      <tr>
        <td style="padding: 12px 16px; color: white;">
          <div style="font-size: 12px; font-weight: bold;">Interested? Let's Talk!</div>
          <div style="font-size: 9px; opacity: 0.85; margin-top: 2px;">Get personalized advice from our experts</div>
        </td>
        <td style="padding: 12px 16px; color: white; text-align: right;">
          <div style="font-size: 10px; font-weight: bold;">hello@aileadmantra.com</div>
          <div style="font-size: 9px; opacity: 0.8; margin-top: 2px;">aileadmantra.com</div>
        </td>
      </tr>
    </table>

  </div>

  <!-- FOOTER -->
  <table style="width: 100%; background: #f5f5f5; border-top: 2px solid #0a5c44; border-collapse: collapse;">
    <tr>
      <td style="padding: 8px 30px; color: #888; font-size: 9px;">Generated by AILeadMantra.com</td>
      <td style="padding: 8px 30px; color: #0a5c44; font-size: 9px; font-weight: bold; text-align: right;">Powered by AI</td>
    </tr>
  </table>

</div>
      `;

      document.body.appendChild(element);

      // Convert HTML to Canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      document.body.removeChild(element);

      // Create PDF - fit content exactly, no extra space
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      if (imgHeight <= pdfHeight) {
        // Fits in one page - place at top, no extra space
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // Multi-page - proper splitting
        let yOffset = 0;
        let remainingHeight = imgHeight;
        let pageNum = 0;
        
        while (remainingHeight > 0) {
          if (pageNum > 0) pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, -yOffset, imgWidth, imgHeight);
          yOffset += pdfHeight;
          remainingHeight -= pdfHeight;
          pageNum++;
        }
      }
      
      // Generate filename
      const safeTitle = title.replace(/[^a-zA-Z0-9]/g, '-').substring(0, 30);
      const filename = `AILeadMantra-Analysis-${safeTitle}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
      
      toast.success('PDF Downloaded Successfully!', { duration: 3000 });
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      if (selectedDoc?.id === docId) setSelectedDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const viewDocument = async (doc) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDoc(res.data);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ========== SHARING FUNCTIONS ==========
  
  const openShareModal = async () => {
    if (!selectedDoc) return;
    
    setShowShareModal(true);
    setSharingLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/documents/${selectedDoc.id}/share-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareStatus(res.data);
    } catch (error) {
      console.error('Failed to get share status:', error);
      setShareStatus({ is_public: false });
    } finally {
      setSharingLoading(false);
    }
  };

  const toggleSharing = async (enable) => {
    if (!selectedDoc) return;
    
    setSharingLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/documents/${selectedDoc.id}/share`,
        { is_public: enable },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShareStatus(res.data);
      
      if (enable) {
        toast.success('Public link created! Link expires in 30 days.');
      } else {
        toast.success('Public sharing disabled');
      }
      
      // Update selected doc
      setSelectedDoc(prev => ({
        ...prev,
        is_public: res.data.is_public,
        public_token: res.data.public_token,
        public_view_count: res.data.view_count
      }));
      
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update sharing settings');
    } finally {
      setSharingLoading(false);
    }
  };

  const copyPublicLink = async () => {
    if (!shareStatus?.public_token) return;
    
    const link = `${window.location.origin}/doc/${shareStatus.public_token}`;
    await navigator.clipboard.writeText(link);
    toast.success('Public link copied to clipboard!');
  };

  // ========== ATTACH TO LEAD FUNCTIONS ==========

  const openAttachModal = async () => {
    setShowAttachModal(true);
    setLeadSearchQuery('');
    await searchLeads('');
  };

  const searchLeads = async (query) => {
    setSearchingLeads(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/documents/leads/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(res.data.leads || []);
    } catch (error) {
      console.error('Failed to search leads:', error);
      setLeads([]);
    } finally {
      setSearchingLeads(false);
    }
  };

  const handleLeadSearch = (e) => {
    const query = e.target.value;
    setLeadSearchQuery(query);
    searchLeads(query);
  };

  const attachToLead = async (lead) => {
    if (!selectedDoc) return;
    
    setAttachingLead(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/documents/${selectedDoc.id}/attach-lead`,
        { lead_id: lead.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Document attached to ${lead.name}`);
      setShowAttachModal(false);
      
      // Update selected doc
      setSelectedDoc(prev => ({
        ...prev,
        attached_lead_id: lead.id,
        attached_lead_name: lead.name
      }));
      
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to attach document to lead');
    } finally {
      setAttachingLead(false);
    }
  };

  const detachFromLead = async () => {
    if (!selectedDoc || !selectedDoc.attached_lead_id) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/documents/${selectedDoc.id}/detach-lead`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Document detached from lead');
      
      // Update selected doc
      setSelectedDoc(prev => ({
        ...prev,
        attached_lead_id: null,
        attached_lead_name: null
      }));
      
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to detach document');
    }
  };

  return (
    <div data-testid="doc-analyzer-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Smart Document Analyzer</h1>
        <p className="text-sm md:text-base text-emerald-300/70">Upload documents and extract key insights with AI</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Left Column - Upload & History */}
        <div className="lg:col-span-1 space-y-4 md:space-y-6">
          {/* Upload Zone */}
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-3 md:pb-4">
              <CardTitle className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
                <Upload className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
                Upload Document
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 md:pt-4">
              <div
                data-testid="upload-zone"
                className={`relative border-2 border-dashed rounded-xl p-4 md:p-8 text-center transition-all cursor-pointer
                  ${dragActive 
                    ? 'border-emerald-500 bg-emerald-500/10' 
                    : 'border-emerald-800/50 hover:border-emerald-600/50 hover:bg-emerald-900/20'
                  }
                  ${uploading ? 'pointer-events-none opacity-70' : ''}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
                
                {uploading ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {analyzing ? 'Analyzing with AI...' : 'Uploading...'}
                      </p>
                      <div className="mt-3 w-full bg-emerald-900/50 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-emerald-300/70 mt-2">{uploadProgress}%</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                      <FileText className="w-8 h-8 text-emerald-400" />
                    </div>
                    <p className="text-white font-medium mb-1">
                      Drop your document here
                    </p>
                    <p className="text-sm text-emerald-300/70 mb-3">
                      or click to browse
                    </p>
                    <p className="text-xs text-emerald-300/50">
                      PDF, Word, PNG, JPG (Max 10MB)
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Document History */}
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-400" />
                  Recent Documents
                </span>
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  {documents.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                  <p className="text-emerald-300/50 text-sm">No documents yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg border cursor-pointer transition-all group
                        ${selectedDoc?.id === doc.id 
                          ? 'bg-emerald-600/20 border-emerald-600/50' 
                          : 'bg-[#0a3d2e]/30 border-emerald-800/30 hover:border-emerald-600/30'
                        }
                      `}
                      onClick={() => viewDocument(doc)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm truncate">
                            {doc.analysis?.title || doc.filename}
                          </p>
                          <p className="text-emerald-300/50 text-xs mt-1">
                            {formatDate(doc.created_at)} • {formatFileSize(doc.size)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.status === 'analyzed' && (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          )}
                          {doc.status === 'analyzing' && (
                            <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                          )}
                          {doc.status === 'failed' && (
                            <AlertCircle className="w-4 h-4 text-red-400" />
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteDocument(doc.id);
                            }}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Analysis Results */}
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            {selectedDoc?.analysis ? (
              <motion.div
                key={selectedDoc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Document Header */}
                <Card className="bg-[#052e22]/50 border-emerald-800/30">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <Badge className={`mb-3 ${DOC_TYPE_COLORS[selectedDoc.analysis.document_type] || DOC_TYPE_COLORS['General Document']}`}>
                          {selectedDoc.analysis.document_type}
                        </Badge>
                        <h2 className="text-2xl font-bold text-white mb-2">
                          {selectedDoc.analysis.title}
                        </h2>
                        <p className="text-emerald-300/80 leading-relaxed">
                          {selectedDoc.analysis.summary}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedDoc(null)}
                        className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-emerald-400" />
                      </button>
                    </div>

                    {/* Attached Lead Badge */}
                    {selectedDoc.attached_lead_name && (
                      <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-purple-400" />
                          <span className="text-purple-300 text-sm">
                            Attached to: <strong>{selectedDoc.attached_lead_name}</strong>
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={detachFromLead}
                          className="text-purple-400 hover:bg-purple-900/30"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {/* Share Buttons */}
                    <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-emerald-800/30">
                      {/* Public Share Link */}
                      <Button
                        data-testid="share-public-btn"
                        onClick={openShareModal}
                        className={`${selectedDoc.is_public ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-600 hover:bg-slate-700'} text-white`}
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        {selectedDoc.is_public ? 'Public Link' : 'Share Link'}
                        {selectedDoc.is_public && selectedDoc.public_view_count > 0 && (
                          <Badge className="ml-2 bg-white/20 text-white text-xs">
                            {selectedDoc.public_view_count} views
                          </Badge>
                        )}
                      </Button>
                      
                      {/* Attach to Lead */}
                      <Button
                        data-testid="attach-lead-btn"
                        onClick={openAttachModal}
                        variant="outline"
                        className={`${selectedDoc.attached_lead_id 
                          ? 'border-purple-500/50 text-purple-400 bg-purple-500/10' 
                          : 'border-emerald-800/50 text-emerald-400'} hover:bg-emerald-900/30`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        {selectedDoc.attached_lead_id ? 'Change Lead' : 'Attach to Lead'}
                      </Button>
                      
                      <Button
                        data-testid="copy-btn"
                        onClick={copyBulletPoints}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Points
                      </Button>
                      <Button
                        data-testid="whatsapp-btn"
                        onClick={shareViaWhatsApp}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                      <Button
                        data-testid="email-btn"
                        onClick={shareViaEmail}
                        variant="outline"
                        className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                      <Button
                        data-testid="download-pdf-btn"
                        onClick={downloadPDF}
                        disabled={generatingPDF}
                        className="bg-[#0a5c44] hover:bg-[#0a6b50] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingPDF ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Key Points */}
                <Card className="bg-[#052e22]/50 border-emerald-800/30">
                  <CardHeader className="border-b border-emerald-800/30 pb-4">
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      <Tag className="w-5 h-5 text-emerald-400" />
                      Key Points
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ul className="space-y-3">
                      {selectedDoc.analysis.key_points.map((point, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-start gap-3 text-emerald-100"
                        >
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs font-medium text-emerald-400">
                            {index + 1}
                          </span>
                          <span className="leading-relaxed">{point}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Additional Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Dates */}
                  {selectedDoc.analysis.dates && selectedDoc.analysis.dates.length > 0 && (
                    <Card className="bg-[#052e22]/50 border-emerald-800/30">
                      <CardHeader className="border-b border-emerald-800/30 pb-4">
                        <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-emerald-400" />
                          Important Dates
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <ul className="space-y-2">
                          {selectedDoc.analysis.dates.map((date, index) => (
                            <li key={index} className="flex items-center gap-2 text-emerald-200 text-sm">
                              <ChevronRight className="w-4 h-4 text-emerald-500" />
                              {date}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pricing */}
                  {selectedDoc.analysis.pricing && selectedDoc.analysis.pricing.length > 0 && (
                    <Card className="bg-[#052e22]/50 border-emerald-800/30">
                      <CardHeader className="border-b border-emerald-800/30 pb-4">
                        <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-emerald-400" />
                          Pricing Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <ul className="space-y-2">
                          {selectedDoc.analysis.pricing.map((price, index) => (
                            <li key={index} className="flex items-center gap-2 text-emerald-200 text-sm">
                              <ChevronRight className="w-4 h-4 text-emerald-500" />
                              {price}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Key Terms */}
                {selectedDoc.analysis.key_terms && selectedDoc.analysis.key_terms.length > 0 && (
                  <Card className="bg-[#052e22]/50 border-emerald-800/30">
                    <CardHeader className="border-b border-emerald-800/30 pb-4">
                      <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                        <FileType className="w-5 h-5 text-emerald-400" />
                        Key Terms & Conditions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="flex flex-wrap gap-2">
                        {selectedDoc.analysis.key_terms.map((term, index) => (
                          <Badge
                            key={index}
                            className="bg-emerald-900/50 text-emerald-300 border border-emerald-800/50"
                          >
                            {term}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="h-full flex items-center justify-center"
              >
                <Card className="bg-[#052e22]/30 border-emerald-800/30 border-dashed w-full">
                  <CardContent className="py-20 text-center">
                    <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-6">
                      <FileText className="w-10 h-10 text-emerald-700" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      No Document Selected
                    </h3>
                    <p className="text-emerald-300/70 max-w-md mx-auto">
                      Upload a document to get started. Our AI will extract key bullet points, 
                      identify important dates, pricing info, and more.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#052e22] border border-emerald-800/30 rounded-2xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-emerald-800/30">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-emerald-400" />
                    Public Share Link
                  </h2>
                  <button 
                    onClick={() => setShowShareModal(false)}
                    className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-emerald-400" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {sharingLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Toggle Switch */}
                    <div className="flex items-center justify-between p-4 bg-[#0a3d2e] rounded-lg">
                      <div>
                        <p className="text-white font-medium">Enable Public Link</p>
                        <p className="text-sm text-emerald-300/70">Anyone with the link can view</p>
                      </div>
                      <button
                        onClick={() => toggleSharing(!shareStatus?.is_public)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          shareStatus?.is_public ? 'bg-emerald-500' : 'bg-slate-600'
                        }`}
                      >
                        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          shareStatus?.is_public ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>

                    {shareStatus?.is_public && shareStatus?.public_token && (
                      <>
                        {/* Link Display */}
                        <div className="space-y-2">
                          <label className="text-sm text-emerald-300/70">Public URL</label>
                          <div className="flex gap-2">
                            <Input
                              readOnly
                              value={`${window.location.origin}/doc/${shareStatus.public_token}`}
                              className="bg-[#0a3d2e] border-emerald-800/30 text-white font-mono text-sm"
                            />
                            <Button
                              onClick={copyPublicLink}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-[#0a3d2e] rounded-lg text-center">
                            <Eye className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-white">{shareStatus.view_count || 0}</p>
                            <p className="text-xs text-emerald-300/70">Total Views</p>
                          </div>
                          <div className="p-4 bg-[#0a3d2e] rounded-lg text-center">
                            <Clock className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-white">30 Days</p>
                            <p className="text-xs text-emerald-300/70">Link Expires</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attach to Lead Modal */}
      <AnimatePresence>
        {showAttachModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAttachModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#052e22] border border-emerald-800/30 rounded-2xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-emerald-800/30">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-emerald-400" />
                    Attach to Lead
                  </h2>
                  <button 
                    onClick={() => setShowAttachModal(false)}
                    className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-emerald-400" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-emerald-500/50" />
                  <Input
                    data-testid="search-lead-input"
                    placeholder="Search leads by name or email..."
                    value={leadSearchQuery}
                    onChange={handleLeadSearch}
                    className="pl-10 bg-[#0a3d2e] border-emerald-800/30 text-white placeholder:text-emerald-300/40"
                  />
                </div>

                {/* Leads List */}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {searchingLeads ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                    </div>
                  ) : leads.length === 0 ? (
                    <div className="text-center py-8">
                      <User className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                      <p className="text-emerald-300/50 text-sm">
                        {leadSearchQuery ? 'No leads found' : 'Start typing to search leads'}
                      </p>
                    </div>
                  ) : (
                    leads.map((lead) => (
                      <button
                        key={lead.id}
                        data-testid={`lead-option-${lead.id}`}
                        onClick={() => attachToLead(lead)}
                        disabled={attachingLead}
                        className="w-full p-3 rounded-lg border border-emerald-800/30 bg-[#0a3d2e] hover:border-emerald-600/50 transition-all text-left flex items-center justify-between group disabled:opacity-50"
                      >
                        <div>
                          <p className="text-white font-medium">{lead.name}</p>
                          <p className="text-sm text-emerald-300/70">{lead.email}</p>
                          {lead.company && (
                            <p className="text-xs text-emerald-300/50">{lead.company}</p>
                          )}
                        </div>
                        <Check className="w-5 h-5 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocAnalyzer;
